/**
 * Intents that act on the machine rather than the music: speed, pitch, power,
 * the cue lever, the camera, and the two look-and-feel preferences the deck
 * itself reacts to.
 */

import { RPM_45 } from '../geometry/dims.ts';
import * as player from '../state/player.ts';
import { togglePref } from '../state/prefs.ts';
import * as ui from '../state/ui.ts';
import { HOME_VIEW, type ViewName } from '../three/views.ts';
import { hardware } from './hardware.ts';

export function applyView(name: ViewName): void {
  void hardware().stage.applyView(name);
  ui.setView(name);
}

/** Back to the framing the deck opened with, wherever the camera has been taken. */
export const resetView = (): void => {
  applyView(HOME_VIEW);
};

/** Applies the pitch fader, and says so when YouTube cannot follow it. */
export function applyPitch(pct: number, announce = false): void {
  const { tt } = hardware();
  tt.setPitch(pct);
  const { applied, requested } = player.deck.setPitch(tt.state.pitch);
  player.setPitch(tt.state.pitch);
  player.setRpm(tt.state.rpm === RPM_45 ? 45 : 33);
  if (player.deck.playing) tt.setSpinning(true);
  if (announce && player.deck.source === 'youtube' && Math.abs(applied - requested) > 0.01) {
    ui.toast('YouTube only allows fixed playback speeds — the platter shows the true pitch', 3200);
  }
}

export const nudgePitch = (delta: number): void => {
  applyPitch(hardware().tt.state.pitch + delta, true);
};

export function setRPM(rpm: number): void {
  const { tt } = hardware();
  tt.setRPM(rpm);
  player.setRpm(rpm === 45 ? 45 : 33);
  player.setPitch(tt.state.pitch);
  if (player.deck.playing) tt.setSpinning(true);
  ui.toast(rpm === 45 ? '45 RPM' : '33⅓ RPM', 1200);
}

export const toggleRPM = (): void => {
  setRPM(hardware().tt.state.rpm === RPM_45 ? 33 : 45);
};

export function togglePower(): void {
  const { tt } = hardware();
  const on = !tt.state.powered;
  tt.setPowered(on);
  if (on) {
    ui.toast('Deck on', 1200);
    if (player.deck.playing) tt.setSpinning(true);
  } else {
    player.deck.pause();
    ui.toast('Deck off', 1200);
  }
}

export function toggleCue(): void {
  const { tt } = hardware();
  if (tt.state.armDown) {
    void tt.setArmDown(false, 380);
    player.deck.pause();
    return;
  }
  void lowerCue();
}

/** A parked arm swings over the groove before it drops; one already there just drops. */
async function lowerCue(): Promise<void> {
  const { tt } = hardware();
  if (tt.state.armFollow) void tt.setArmDown(true, 380);
  else await tt.dropNeedle();
  void player.deck.demo.sfxNeedleDrop().catch(() => undefined);
  if (!player.deck.playing) void player.deck.play();
}

export function togglePictureDisc(): void {
  const on = togglePref('pictureDisc');
  const art = player.artwork();
  hardware().tt.setPictureDisc(on, art?.image ?? null, art?.palette ?? null);
  ui.toast(on ? 'Picture disc on' : 'Picture disc off', 1300);
}

export function toggleCinematic(): void {
  const on = togglePref('cinematicSwap');
  ui.toast(on ? 'Cinematic record change on' : 'Instant record change', 1500);
}

export function toggleCrackle(): void {
  player.deck.demo.setCrackle(togglePref('crackle'));
}

export const toggleYouTubeCard = (): void => {
  ui.setYoutubeCardOpen((open) => !open);
};
